import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseMutationOptions
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api';

/**
 * ── The optimistic update contract ──────────────────────────────────────────
 *
 * Every mutation in this app moves the UI *before* the network call and rolls
 * back if the server disagrees. The v1 app round-tripped every write, which is
 * the main reason it felt slow — so this is deliberately the path of least
 * resistance rather than an opt-in.
 *
 * `useOptimisticMutation` handles the three things that are easy to get wrong:
 *
 *  1. **Canceling in-flight queries** before writing to the cache. Without
 *     this, a refetch that started before the mutation can land afterward and
 *     silently overwrite the optimistic value.
 *  2. **Snapshotting every affected key** and restoring all of them on error,
 *     not just the one that was patched.
 *  3. **Invalidating only after settle**, so a failed rollback isn't
 *     immediately re-clobbered by a refetch of stale data.
 */

type Patcher<TData, TVars> = (draft: TData, vars: TVars) => TData;

export interface OptimisticSpec<TVars> {
  /** Keys to patch. Each patch runs against whatever is currently cached. */
  patches: {
    key: QueryKey;
    /** Runs for every cached query whose key is a prefix match. */
    exact?: boolean;
    update: Patcher<never, TVars>;
  }[];
  /** Keys to invalidate once the mutation settles (success or failure). */
  invalidate?: QueryKey[];
}

export interface UseOptimisticMutationArgs<TData, TVars, TResult>
  extends Omit<
    UseMutationOptions<TResult, unknown, TVars, OptimisticContext>,
    'mutationFn' | 'onMutate' | 'onError' | 'onSettled'
  > {
  mutationFn: (vars: TVars) => Promise<TResult>;
  optimistic: (vars: TVars, qc: QueryClient) => OptimisticSpec<TVars>;
  /** Message shown on failure, above the server's own error text. */
  errorMessage?: string;
  successMessage?: string | ((result: TResult, vars: TVars) => string | null);
  onSuccessSideEffect?: (result: TResult, vars: TVars, qc: QueryClient) => void;
  _data?: TData;
}

interface OptimisticContext {
  snapshots: [QueryKey, unknown][];
  invalidate: QueryKey[];
}

export function useOptimisticMutation<TData, TVars, TResult = unknown>({
  mutationFn,
  optimistic,
  errorMessage = 'That change could not be saved.',
  successMessage,
  onSuccessSideEffect,
  ...rest
}: UseOptimisticMutationArgs<TData, TVars, TResult>) {
  const qc = useQueryClient();

  return useMutation<TResult, unknown, TVars, OptimisticContext>({
    mutationFn,
    async onMutate(vars) {
      const spec = optimistic(vars, qc);
      const snapshots: [QueryKey, unknown][] = [];

      for (const patch of spec.patches) {
        // Stop anything in flight that could land on top of our patch.
        await qc.cancelQueries({ queryKey: patch.key, exact: patch.exact });

        const matches = qc.getQueriesData({ queryKey: patch.key, exact: patch.exact });
        for (const [key, data] of matches) {
          if (data === undefined) continue;
          snapshots.push([key, data]);
          const update = patch.update as unknown as Patcher<unknown, TVars>;
          qc.setQueryData(key, (current: unknown) =>
            current === undefined ? current : update(current, vars)
          );
        }
      }

      return { snapshots, invalidate: spec.invalidate ?? [] };
    },
    onError(error, _vars, context) {
      // Put every touched key back exactly as it was.
      for (const [key, data] of context?.snapshots ?? []) {
        qc.setQueryData(key, data);
      }
      const detail = error instanceof ApiError ? error.message : undefined;
      toast.error(errorMessage, { description: detail });
    },
    onSuccess(result, vars) {
      onSuccessSideEffect?.(result, vars, qc);
      const message =
        typeof successMessage === 'function' ? successMessage(result, vars) : successMessage;
      if (message) toast.success(message);
    },
    onSettled(_result, _error, _vars, context) {
      for (const key of context?.invalidate ?? []) {
        void qc.invalidateQueries({ queryKey: key });
      }
    },
    ...rest
  });
}

// ------------------------------------------------------- list patch helpers

/** Replace one item in an array by id. */
export function replaceById<T extends { id: string }>(list: T[], id: string, patch: Partial<T>) {
  return list.map((item) => (item.id === id ? { ...item, ...patch } : item));
}

export function removeById<T extends { id: string }>(list: T[], id: string) {
  return list.filter((item) => item.id !== id);
}

/** Shape shared by every paginated cache entry in this app. */
export interface PagedCache<T> {
  items: T[];
  totalCount: number;
}

/**
 * Patch the items of a paginated cache, keeping `totalCount` honest so the
 * pagination footer doesn't briefly disagree with the rows on screen.
 */
export function patchPaged<C extends PagedCache<unknown>>(
  cache: C,
  fn: (items: C['items']) => C['items']
): C {
  const items = fn(cache.items);
  return {
    ...cache,
    items,
    totalCount: cache.totalCount + (items.length - cache.items.length)
  };
}

export function prependPaged<C extends PagedCache<unknown>>(
  cache: C,
  item: C['items'][number]
): C {
  return { ...cache, items: [item, ...cache.items], totalCount: cache.totalCount + 1 };
}
